import { createClient } from '@supabase/supabase-js';

const DEFAULT_SUPABASE_URL = 'http://localhost';
const DEFAULT_SUPABASE_ANON_KEY = 'local-dev-placeholder';

export type SupabaseConnectionConfig = {
  url: string;
  anonKey: string;
};

const SUPABASE_STORAGE_KEY = 'supabase_connection';

const readEnvConfig = (): SupabaseConnectionConfig | null => {
  try {
    const envAny = (process as any)?.env || {};
    const url = typeof envAny.REACT_APP_SUPABASE_URL === 'string' ? String(envAny.REACT_APP_SUPABASE_URL).trim() : '';
    const anonKey = typeof envAny.REACT_APP_SUPABASE_ANON_KEY === 'string' ? String(envAny.REACT_APP_SUPABASE_ANON_KEY).trim() : '';
    if (!url || !anonKey) return null;
    return { url, anonKey };
  } catch {
    return null;
  }
};

const readLocalConfig = (): SupabaseConnectionConfig | null => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(SUPABASE_STORAGE_KEY);
    if (!raw) return null;
    const j = JSON.parse(raw);
    const url = typeof j?.url === 'string' ? String(j.url).trim() : '';
    const anonKey = typeof j?.anonKey === 'string' ? String(j.anonKey).trim() : '';
    if (!url || !anonKey) return null;
    return { url, anonKey };
  } catch {
    return null;
  }
};

const writeLocalConfig = (cfg: SupabaseConnectionConfig) => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SUPABASE_STORAGE_KEY, JSON.stringify({
      url: String(cfg.url || ''),
      anonKey: String(cfg.anonKey || ''),
    }));
  } catch {
    // ignore
  }
};

let supabaseConfig: SupabaseConnectionConfig = readEnvConfig() || readLocalConfig() || {
  url: DEFAULT_SUPABASE_URL,
  anonKey: DEFAULT_SUPABASE_ANON_KEY,
};

const createSupabaseClient = (cfg: SupabaseConnectionConfig) =>
  createClient(cfg.url, cfg.anonKey, {
    db: {
      schema: 'public',
    },
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  });

export let supabase = createSupabaseClient(supabaseConfig);

export const getSupabaseConfig = (): SupabaseConnectionConfig => supabaseConfig;

export const isSupabaseConfigured = (): boolean => {
  const cfg = getSupabaseConfig();
  if (!cfg?.url || !cfg?.anonKey) return false;
  return !(cfg.url === DEFAULT_SUPABASE_URL && cfg.anonKey === DEFAULT_SUPABASE_ANON_KEY);
};

export const ensureSupabaseConfigured = () => {
  if (!isSupabaseConfigured()) {
    throw new Error('No Supabase connection is configured yet. Please set up the project first in the Admin Dashboard (Supabase Connection).');
  }
};

export const configureSupabase = (cfg: SupabaseConnectionConfig) => {
  supabaseConfig = { url: String(cfg.url || ''), anonKey: String(cfg.anonKey || '') };
  writeLocalConfig(supabaseConfig);
  supabase = createSupabaseClient(supabaseConfig);
};

export const clearSupabaseCache = () => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(SUPABASE_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const httpGetJson = async (url: string): Promise<{ ok: boolean; status: number; json: any | null }> => {
  try {
    if (typeof window !== 'undefined' && typeof (window as any).fetch === 'function') {
      const res = await fetch(url, { method: 'GET' });
      const json = await res.json().catch(() => null);
      return { ok: !!res.ok, status: (res as any).status || 0, json };
    }
  } catch {
    // ignore
  }

  return await new Promise((resolve) => {
    try {
      if (typeof window === 'undefined') return resolve({ ok: false, status: 0, json: null });
      const Xhr = (window as any).XMLHttpRequest;
      if (!Xhr) return resolve({ ok: false, status: 0, json: null });
      const xhr = new Xhr();
      xhr.open('GET', url, true);
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) return;
        const status = Number((xhr as any).status || 0);
        const ok = status >= 200 && status < 300;
        let json: any | null = null;
        try {
          json = JSON.parse(String((xhr as any).responseText || ''));
        } catch {
          json = null;
        }
        resolve({ ok, status, json });
      };
      xhr.send(null);
    } catch {
      resolve({ ok: false, status: 0, json: null });
    }
  });
};

export const initSupabase = async (): Promise<boolean> => {
  // If already configured (e.g. by Admin settings page), don't redo initialization.
  if (isSupabaseConfigured()) return true;

  const envCfg = readEnvConfig();
  if (envCfg) {
    configureSupabase(envCfg);
    return true;
  }
  const cached = readLocalConfig();
  if (cached) {
    configureSupabase(cached);
    return true;
  }
  try {
    const endpoints: string[] = ['/api/supabase-connection.php'];
    try {
      if (typeof window !== 'undefined') {
        const host = String(window.location.hostname || '').trim();
        const proto = String(window.location.protocol || 'http:');
        if (host) {
          endpoints.push(`${proto}//${host}:8000/api/supabase-connection.php`);
        }
      }
    } catch {
      // ignore
    }

    for (const endpoint of endpoints) {
      const { ok, json } = await httpGetJson(endpoint);
      const cfg = json && typeof json === 'object' ? (json as any).supabase : null;
      const url = cfg && typeof cfg.url === 'string' ? String(cfg.url).trim() : '';
      const anonKey = cfg && typeof cfg.anonKey === 'string' ? String(cfg.anonKey).trim() : '';
      if (ok && json && (json as any).ok && url && anonKey) {
        configureSupabase({ url, anonKey });
        return true;
      }
    }
  } catch {
    // ignore
  }

  // Fallback: some deployments expose the config JSON under /storage
  // (e.g. storage/integrations/supabase/connection.json). This keeps admin
  // panels functional even when PHP endpoints are not available in dev.
  try {
    const candidates = [
      '/storage/integrations/supabase/connection.json',
      '/storage/connection.json',
    ];

    for (const path of candidates) {
      const { ok, json } = await httpGetJson(path);
      const cfg = (json && typeof json === 'object') ? (json as any) : null;
      const raw = cfg && cfg.supabase && typeof cfg.supabase === 'object' ? cfg.supabase : cfg;
      const url = raw && typeof raw.url === 'string' ? String(raw.url).trim() : '';
      const anonKey = raw && typeof raw.anonKey === 'string' ? String(raw.anonKey).trim() : '';
      if (ok && url && anonKey) {
        configureSupabase({ url, anonKey });
        return true;
      }
    }
  } catch {
    // ignore
  }

  return false;
};

// Database types
export interface Database {
  public: {
    Tables: {
        inquiries: {
          Row: {
            id: string;
            profile_user_id: string;
            requester_user_id: string;
            status: 'pending' | 'approved' | 'rejected' | 'cancelled';
            message: string | null;
            created_at: string;
            updated_at: string;
            responded_at: string | null;
          };
          Insert: {
            id?: string;
            profile_user_id: string;
            requester_user_id: string;
            status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
            message?: string | null;
            created_at?: string;
            updated_at?: string;
            responded_at?: string | null;
          };
          Update: {
            id?: string;
            profile_user_id?: string;
            requester_user_id?: string;
            status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
            message?: string | null;
            created_at?: string;
            updated_at?: string;
            responded_at?: string | null;
          };
        };
        users: {
          Row: {
            id: string;
            email: string;
            first_name: string | null;
            last_name: string | null;
            is_admin: boolean;
            user_status_preference: 'consumer' | 'vendor' | 'applicant' | 'member';
            bio: string | null;
            location: string | null;
            website: string | null;
            social_media_links: Record<string, string> | null;
            interests: string[] | null;
            is_public: boolean;
            notifications_enabled: boolean;
            selected_address_id: string | null;
            alias: string | null;
            alias_last_changed: string | null;
            company_id: string | null;
            created_at: string;
            updated_at: string;
          };
          Insert: {
            id: string;
            email: string;
            first_name?: string | null;
            last_name?: string | null;
            is_admin?: boolean;
            user_status_preference?: 'consumer' | 'vendor' | 'applicant' | 'member';
            bio?: string | null;
            location?: string | null;
            website?: string | null;
            social_media_links?: Record<string, string> | null;
            interests?: string[] | null;
            is_public?: boolean;
            notifications_enabled?: boolean;
            selected_address_id?: string | null;
            alias?: string | null;
            alias_last_changed?: string | null;
            company_id?: string | null;
            created_at?: string;
            updated_at?: string;
          };
          Update: {
            id?: string;
            email?: string;
            first_name?: string | null;
            last_name?: string | null;
            is_admin?: boolean;
            user_status_preference?: 'consumer' | 'vendor' | 'applicant' | 'member';
            bio?: string | null;
            location?: string | null;
            website?: string | null;
            social_media_links?: Record<string, string> | null;
            interests?: string[] | null;
            is_public?: boolean;
            notifications_enabled?: boolean;
            selected_address_id?: string | null;
            alias?: string | null;
            alias_last_changed?: string | null;
            company_id?: string | null;
            created_at?: string;
            updated_at?: string;
          };
        };
      categories: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          image_url: string | null;
          product_count: number;
          is_active: boolean;
          is_job_category: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          image_url?: string | null;
          product_count?: number;
          is_active?: boolean;
          is_job_category?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          image_url?: string | null;
          product_count?: number;
          is_active?: boolean;
          is_job_category?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      subcategories: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          category_id: string;
          image_url: string | null;
          product_count: number;
          is_active: boolean;
          is_job_subcategory: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          category_id: string;
          image_url?: string | null;
          product_count?: number;
          is_active?: boolean;
          is_job_subcategory?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          category_id?: string;
          image_url?: string | null;
          product_count?: number;
          is_active?: boolean;
          is_job_subcategory?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      companies: {
        Row: {
          id: string;
          name: string;
          slug: string;
          description: string | null;
          tagline: string | null;
          industry: string | null;
          company_size: string | null;
          company_type: 'private' | 'public' | 'nonprofit' | 'government' | 'startup';
          website: string | null;
          email: string | null;
          phone: string | null;
          linkedin_url: string | null;
          twitter_url: string | null;
          facebook_url: string | null;
          instagram_url: string | null;
          headquarters_address: string | null;
          headquarters_city: string | null;
          headquarters_state: string | null;
          headquarters_country: string | null;
          headquarters_postal_code: string | null;
          is_remote_friendly: boolean;
          remote_policy: string | null;
          logo_url: string | null;
          cover_image_url: string | null;
          brand_colors: any | null;
          founded_year: number | null;
          revenue_range: string | null;
          employee_count: number | null;
          mission_statement: string | null;
          values: string[] | null;
          benefits: string[] | null;
          perks: string[] | null;
          glassdoor_rating: number | null;
          glassdoor_reviews_count: number | null;
          indeed_rating: number | null;
          indeed_reviews_count: number | null;
          is_verified: boolean;
          is_featured: boolean;
          is_active: boolean;
          is_hiring: boolean;
          profile_views: number;
          job_views: number;
          applications_received: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          slug?: string;
          description?: string | null;
          tagline?: string | null;
          industry?: string | null;
          company_size?: string | null;
          company_type?: 'private' | 'public' | 'nonprofit' | 'government' | 'startup';
          website?: string | null;
          email?: string | null;
          phone?: string | null;
          linkedin_url?: string | null;
          twitter_url?: string | null;
          facebook_url?: string | null;
          instagram_url?: string | null;
          headquarters_address?: string | null;
          headquarters_city?: string | null;
          headquarters_state?: string | null;
          headquarters_country?: string | null;
          headquarters_postal_code?: string | null;
          is_remote_friendly?: boolean;
          remote_policy?: string | null;
          logo_url?: string | null;
          cover_image_url?: string | null;
          brand_colors?: any | null;
          founded_year?: number | null;
          revenue_range?: string | null;
          employee_count?: number | null;
          mission_statement?: string | null;
          values?: string[] | null;
          benefits?: string[] | null;
          perks?: string[] | null;
          glassdoor_rating?: number | null;
          glassdoor_reviews_count?: number | null;
          indeed_rating?: number | null;
          indeed_reviews_count?: number | null;
          is_verified?: boolean;
          is_featured?: boolean;
          is_active?: boolean;
          is_hiring?: boolean;
          profile_views?: number;
          job_views?: number;
          applications_received?: number;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          slug?: string;
          description?: string | null;
          tagline?: string | null;
          industry?: string | null;
          company_size?: string | null;
          company_type?: 'private' | 'public' | 'nonprofit' | 'government' | 'startup';
          website?: string | null;
          email?: string | null;
          phone?: string | null;
          linkedin_url?: string | null;
          twitter_url?: string | null;
          facebook_url?: string | null;
          instagram_url?: string | null;
          headquarters_address?: string | null;
          headquarters_city?: string | null;
          headquarters_state?: string | null;
          headquarters_country?: string | null;
          headquarters_postal_code?: string | null;
          is_remote_friendly?: boolean;
          remote_policy?: string | null;
          logo_url?: string | null;
          cover_image_url?: string | null;
          brand_colors?: any | null;
          founded_year?: number | null;
          revenue_range?: string | null;
          employee_count?: number | null;
          mission_statement?: string | null;
          values?: string[] | null;
          benefits?: string[] | null;
          perks?: string[] | null;
          glassdoor_rating?: number | null;
          glassdoor_reviews_count?: number | null;
          indeed_rating?: number | null;
          indeed_reviews_count?: number | null;
          is_verified?: boolean;
          is_featured?: boolean;
          is_active?: boolean;
          is_hiring?: boolean;
          profile_views?: number;
          job_views?: number;
          applications_received?: number;
          created_at?: string;
          updated_at?: string;
        };
      };
      company_members: {
        Row: {
          id: string;
          company_id: string;
          user_id: string;
          role: 'owner' | 'admin' | 'member' | 'viewer';
          job_title: string | null;
          department: string | null;
          start_date: string | null;
          end_date: string | null;
          is_active: boolean;
          permissions: any | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          company_id: string;
          user_id: string;
          role?: 'owner' | 'admin' | 'member' | 'viewer';
          job_title?: string | null;
          department?: string | null;
          start_date?: string | null;
          end_date?: string | null;
          is_active?: boolean;
          permissions?: any | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          company_id?: string;
          user_id?: string;
          role?: 'owner' | 'admin' | 'member' | 'viewer';
          job_title?: string | null;
          department?: string | null;
          start_date?: string | null;
          end_date?: string | null;
          is_active?: boolean;
          permissions?: any | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      company_jobs: {
        Row: {
          id: string;
          company_id: string;
          title: string;
          description: string;
          requirements: string | null;
          responsibilities: string | null;
          benefits: string | null;
          job_type: 'full-time' | 'part-time' | 'contract' | 'internship' | 'temporary' | 'volunteer';
          employment_type: 'permanent' | 'temporary' | 'contract' | 'internship';
          experience_level: 'entry-level' | 'mid-level' | 'senior-level' | 'executive';
          salary_min: number | null;
          salary_max: number | null;
          salary_currency: string;
          salary_period: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';
          is_remote: boolean;
          location: string | null;
          city: string | null;
          state: string | null;
          country: string | null;
          postal_code: string | null;
          skills_required: string[] | null;
          skills_preferred: string[] | null;
          education_required: string | null;
          experience_years: number | null;
          application_deadline: string | null;
          start_date: string | null;
          is_active: boolean;
          is_featured: boolean;
          application_count: number;
          view_count: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          company_id: string;
          title: string;
          description: string;
          requirements?: string | null;
          responsibilities?: string | null;
          benefits?: string | null;
          job_type?: 'full-time' | 'part-time' | 'contract' | 'internship' | 'temporary' | 'volunteer';
          employment_type?: 'permanent' | 'temporary' | 'contract' | 'internship';
          experience_level?: 'entry-level' | 'mid-level' | 'senior-level' | 'executive';
          salary_min?: number | null;
          salary_max?: number | null;
          salary_currency?: string;
          salary_period?: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';
          is_remote?: boolean;
          location?: string | null;
          city?: string | null;
          state?: string | null;
          country?: string | null;
          postal_code?: string | null;
          skills_required?: string[] | null;
          skills_preferred?: string[] | null;
          education_required?: string | null;
          experience_years?: number | null;
          application_deadline?: string | null;
          start_date?: string | null;
          is_active?: boolean;
          is_featured?: boolean;
          application_count?: number;
          view_count?: number;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          company_id?: string;
          title?: string;
          description?: string;
          requirements?: string | null;
          responsibilities?: string | null;
          benefits?: string | null;
          job_type?: 'full-time' | 'part-time' | 'contract' | 'internship' | 'temporary' | 'volunteer';
          employment_type?: 'permanent' | 'temporary' | 'contract' | 'internship';
          experience_level?: 'entry-level' | 'mid-level' | 'senior-level' | 'executive';
          salary_min?: number | null;
          salary_max?: number | null;
          salary_currency?: string;
          salary_period?: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';
          is_remote?: boolean;
          location?: string | null;
          city?: string | null;
          state?: string | null;
          country?: string | null;
          postal_code?: string | null;
          skills_required?: string[] | null;
          skills_preferred?: string[] | null;
          education_required?: string | null;
          experience_years?: number | null;
          application_deadline?: string | null;
          start_date?: string | null;
          is_active?: boolean;
          is_featured?: boolean;
          application_count?: number;
          view_count?: number;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
  };
}

export type User = Database['public']['Tables']['users']['Row'];
export type UserInsert = Database['public']['Tables']['users']['Insert'];
export type UserUpdate = Database['public']['Tables']['users']['Update'];

export type Category = Database['public']['Tables']['categories']['Row'];
export type CategoryInsert = Database['public']['Tables']['categories']['Insert'];
export type CategoryUpdate = Database['public']['Tables']['categories']['Update'];

export type Subcategory = Database['public']['Tables']['subcategories']['Row'];
export type SubcategoryInsert = Database['public']['Tables']['subcategories']['Insert'];
export type SubcategoryUpdate = Database['public']['Tables']['subcategories']['Update'];

export type Company = Database['public']['Tables']['companies']['Row'];
export type CompanyInsert = Database['public']['Tables']['companies']['Insert'];
export type CompanyUpdate = Database['public']['Tables']['companies']['Update'];

export type CompanyMember = Database['public']['Tables']['company_members']['Row'];
export type CompanyMemberInsert = Database['public']['Tables']['company_members']['Insert'];
export type CompanyMemberUpdate = Database['public']['Tables']['company_members']['Update'];

export type CompanyJob = Database['public']['Tables']['company_jobs']['Row'];
export type CompanyJobInsert = Database['public']['Tables']['company_jobs']['Insert'];
export type CompanyJobUpdate = Database['public']['Tables']['company_jobs']['Update'];
